import React from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Link2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";

// Amber banner shown while the Google calendar is not connected: explains what is
// blocked (publishing the template + creating slots). `showButton` is turned off by the
// pages that already show GoogleCalendarCard: ONE "Connect" action visible at a time
// (user feedback: two buttons = confusion).
//
// `configured` (B5): the "Connect" button calls POST /oauth/google-calendar/start, which
// answers 503 when the PLATFORM has no Google keys (GOOGLE_CLIENT_ID/SECRET), a problem
// the practitioner cannot solve themselves. `configured=false` switches the text to
// `gcal.notConfigured` (same as GoogleCalendarCard) and removes the button in every case,
// rather than leaving a button that always fails.
export const ConnectGateBanner = ({ showButton = true, configured = true }) => {
  const t = useT("admin");

  const connect = async () => {
    try {
      // A POST: it writes the flow's state, which names an operator who started it.
      const { data } = await api.post("/oauth/google-calendar/start");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(extractApiError(e, t("gcal.errorToast")));
    }
  };

  return (
    <div
      data-testid="gate-banner"
      className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-300 rounded-2xl px-4 py-3"
    >
      <div className="flex items-start gap-3 min-w-0">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="font-medium text-amber-900 text-sm">
            {t(configured ? "gate.title" : "gcal.notConfigured")}
          </div>
          {configured && <p className="text-xs text-amber-800/80 mt-0.5">{t("gate.body")}</p>}
        </div>
      </div>
      {showButton && configured && (
        <button
          onClick={connect}
          data-testid="gate-connect-btn"
          className="inline-flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-full bg-sol-gold text-eh-ink hover:opacity-90 shrink-0"
        >
          <Link2 className="w-4 h-4" /> {t("gcal.connect")}
        </button>
      )}
    </div>
  );
};
